import { useEffect, useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { adminCampaigns, type CampaignOut, type CampaignUpdate } from '../../api/admin';
import {
  Sparkles, Pencil, Check, X, ToggleLeft, ToggleRight, Calendar, ExternalLink,
} from 'lucide-react';

// Dates are stored UTC but admins think in IST (the only market). These mirror
// the helpers used on the Coupons page.
const IST_OFFSET_MIN = 330;
function utcIsoToIstLocal(utcIso: string | null | undefined): string | null {
  if (!utcIso) return null;
  const d = new Date(utcIso);
  if (isNaN(d.getTime())) return null;
  return new Date(d.getTime() + IST_OFFSET_MIN * 60_000).toISOString().slice(0, 16);
}
function istLocalToUtcIso(istLocal: string | null | undefined): string | null {
  if (!istLocal) return null;
  const asIfUtc = new Date(istLocal + 'Z');
  if (isNaN(asIfUtc.getTime())) return null;
  return new Date(asIfUtc.getTime() - IST_OFFSET_MIN * 60_000).toISOString();
}

type Status = 'active' | 'scheduled' | 'expired' | 'inactive';
function campaignStatus(c: CampaignOut): Status {
  if (!c.is_active) return 'inactive';
  const now = Date.now();
  if (c.starts_at && now < new Date(c.starts_at).getTime()) return 'scheduled';
  if (c.ends_at && now > new Date(c.ends_at).getTime()) return 'expired';
  return 'active';
}
const STATUS_BADGE: Record<Status, { bg: string; text: string; label: string }> = {
  active:    { bg: 'bg-emerald-500/10', text: 'text-emerald-500', label: 'Active' },
  scheduled: { bg: 'bg-blue-500/10',    text: 'text-blue-500',    label: 'Scheduled' },
  expired:   { bg: 'bg-amber-500/10',   text: 'text-amber-500',   label: 'Expired' },
  inactive:  { bg: 'bg-gray-500/10',    text: 'text-gray-500',    label: 'Inactive' },
};

interface EditForm {
  offer_text: string;
  cta_label: string;
  cta_link: string;
  starts_at: string | null;
  ends_at: string | null;
}

export default function CampaignsPage() {
  const { isDark } = useTheme();
  const [list, setList] = useState<CampaignOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<EditForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = () => adminCampaigns.list().then(setList);

  useEffect(() => {
    reload().catch(console.error).finally(() => setLoading(false));
  }, []);

  const startEdit = (c: CampaignOut) => {
    setForm({
      offer_text: c.offer_text,
      cta_label: c.cta_label,
      cta_link: c.cta_link,
      starts_at: utcIsoToIstLocal(c.starts_at),
      ends_at: utcIsoToIstLocal(c.ends_at),
    });
    setEditingId(c.id);
    setError(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => { setEditingId(null); setForm(null); setError(null); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingId || !form) return;
    setSaving(true);
    setError(null);
    try {
      const payload: CampaignUpdate = {
        offer_text: form.offer_text,
        cta_label: form.cta_label,
        cta_link: form.cta_link,
        starts_at: istLocalToUtcIso(form.starts_at),
        ends_at: istLocalToUtcIso(form.ends_at),
      };
      await adminCampaigns.update(editingId, payload);
      await reload();
      cancelEdit();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save campaign');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // Enabling one disables the others server-side, so always reload after a toggle.
  const toggleActive = async (c: CampaignOut) => {
    try {
      await adminCampaigns.update(c.id, { is_active: !c.is_active });
      await reload();
    } catch (err) { console.error(err); }
  };

  const inputCls = `w-full px-3 py-2 rounded-lg border text-sm outline-none ${
    isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-200'
  }`;
  const labelCls = `block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const activeCount = list.filter(c => campaignStatus(c) === 'active').length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Sparkles size={24} className="text-emerald-500" /> Campaigns</h1>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {activeCount > 0 ? '1 live on the homepage hero' : 'None live'} &middot; only one can be active at a time
          </p>
        </div>
      </div>

      {/* Edit form */}
      {editingId && form && (
        <form onSubmit={handleSubmit} className={`rounded-xl border p-6 space-y-4 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-lg">Edit Campaign</h2>
            <button type="button" onClick={cancelEdit} className="text-gray-400 hover:text-gray-300" title="Cancel"><X size={18} /></button>
          </div>
          {error && <div className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-500 text-sm">{error}</div>}
          <div>
            <label className={labelCls}>Offer Text</label>
            <input type="text" value={form.offer_text} onChange={e => setForm({ ...form, offer_text: e.target.value })} className={inputCls} maxLength={200} placeholder="Light up the festive season with ZeroSmoke" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>CTA Label</label>
              <input type="text" value={form.cta_label} onChange={e => setForm({ ...form, cta_label: e.target.value })} className={inputCls} maxLength={60} placeholder="Shop Diwali Offers" required />
            </div>
            <div>
              <label className={labelCls}>CTA Link</label>
              <input type="text" value={form.cta_link} onChange={e => setForm({ ...form, cta_link: e.target.value })} className={inputCls} maxLength={300} placeholder="/store" />
              <p className={`text-xs mt-1 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>Relative path (/store) or https:// URL</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Starts (IST) — optional</label>
              <input type="datetime-local" value={form.starts_at ?? ''} onChange={e => setForm({ ...form, starts_at: e.target.value || null })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Ends (IST) — optional</label>
              <input type="datetime-local" value={form.ends_at ?? ''} onChange={e => setForm({ ...form, ends_at: e.target.value || null })} className={inputCls} />
            </div>
          </div>
          <button type="submit" disabled={saving} className="flex items-center gap-1.5 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
            <Check size={16} /> {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      )}

      {/* Campaign cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {list.map(c => {
          const status = campaignStatus(c);
          const badge = STATUS_BADGE[status];
          return (
            <div key={c.id} className={`rounded-xl border p-5 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'} ${!c.is_active ? 'opacity-70' : ''}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{c.name}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>{badge.label}</span>
                  </div>
                  <span className={`text-xs font-mono ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{c.slug}</span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => startEdit(c)} className="p-1 text-blue-400 hover:bg-blue-500/10 rounded transition-colors" title="Edit"><Pencil size={15} /></button>
                  <button onClick={() => toggleActive(c)} title={c.is_active ? 'Disable' : 'Enable'}>
                    {c.is_active ? <ToggleRight size={24} className="text-emerald-500" /> : <ToggleLeft size={24} className="text-gray-400" />}
                  </button>
                </div>
              </div>

              <p className={`text-sm mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{c.offer_text || <span className="italic text-gray-500">No offer text</span>}</p>

              <div className={`space-y-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                <p className="flex items-center gap-1"><ExternalLink size={11} /> {c.cta_label} &rarr; {c.cta_link}</p>
                {(c.starts_at || c.ends_at) && (
                  <p className="flex items-center gap-1">
                    <Calendar size={11} />
                    {c.starts_at ? new Date(c.starts_at).toLocaleDateString('en-IN') : 'Any'} – {c.ends_at ? new Date(c.ends_at).toLocaleDateString('en-IN') : 'No end'}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
